import { createContext, useContext } from "react";
import type { StableId } from "@archlang/scene/arch-model";
import type { ProcessPlaybackController } from "../hooks/useProcessPlayback.js";

export interface ProcessPlaybackContextValue extends ProcessPlaybackController {
  readonly activeProcessId: StableId | null;
  readonly handleSelectProcess: (id: StableId | null) => void;
  readonly handleActivateProcess: (id: StableId) => void;
  /** Frame the active step's endpoint nodes on the board (scrubber n/m). */
  readonly handleLocateStep: () => void;
  /** Acting node of the current SELF-REFERENTIAL step — the board rings it
   *  (self-calls / notes / awaits draw no edge to carry the highlight). */
  readonly stepActorId: string | null;
  /** The current step's endpoint node ids (equal for self rows) — drives the
   *  board's step auto-framing. */
  readonly stepParticipants: { readonly aId: string; readonly bId: string } | null;
}

export const ProcessPlaybackContext =
  createContext<ProcessPlaybackContextValue | null>(null);

/** The composition's playback controller. Throws outside the provider. */
export function useProcessPlaybackContext(): ProcessPlaybackContextValue {
  const v = useContext(ProcessPlaybackContext);
  if (!v) {
    throw new Error(
      "useProcessPlaybackContext must be used within a <ProcessPlaybackProvider>",
    );
  }
  return v;
}

/**
 * Like {@link useProcessPlaybackContext} but returns null instead of throwing
 * when no provider is mounted. For primitives that OPTIONALLY surface process
 * playback (the board's step decorations) and must also work in compositions
 * with no playback service — the marketing demo, Studio's preview.
 */
export function useProcessPlaybackContextOptional(): ProcessPlaybackContextValue | null {
  return useContext(ProcessPlaybackContext);
}
